/**
 * Kalendarz firmy PV w panelu „Klienci” (makieta `docs/makiety/kalendarz-firm-pv`, prośba właściciela 2026-10-11).
 *
 * Terminy żyją przy kliencie, w `dane.kalendarz` (lista, bez migracji bazy):
 *   { id, rodzaj: 'spotkanie'|'montaz'|'serwis', od, do (RRRR-MM-DD; `do` różne od `od` tylko przy montażu),
 *     godzOd, godzDo ('HH:MM' albo ''), calyDzien, miejscowosc, ulica, kto, status: 'zaplanowany'|'rozpoczety'|
 *     'zakonczony'|'przeniesiony'|'odwolany', przeniesionyNa, ustalony (pewny termin zamiast przewidywanego), notatka,
 *     ustalenia (co ustalono na odbytym spotkaniu albo serwisie, 2026-10-24; kopia trafia do notatek klienta) }
 * `rozpoczety` (runda 4, 2026-10-11) tylko przy montażu: montaż w dwóch krokach, „Montaż rozpoczęty” i „Montaż
 * zakończony”. Rozpoczęty jest w toku niezależnie od dat i liczy się jak pewny (backend `_STATUSY_TERMINOW`).
 * To jedno źródło dla kalendarza, karty, listy, kanbanu i osi czasu. Stare pojedyncze daty z karty (`dane.terminy`,
 * pierwsza wersja z 2026-10-11) przechodzą do listy przy pierwszym otwarciu (`zTerminowKarty`).
 * Backend wybiera z listy do skrótu klienta (`accounts/api.py` `_handel_klienta`): `kalendarz` i `terminy`.
 *
 * Runda 6 (2026-10-12): `krokiTerminu` i `zastosujKrok`, kroki terminu w toku jako przyciski w kafelku „Terminy”
 * (potwierdzenie z klientem, odbycie, rozpoczęcie i zakończenie montażu) na tych samych regułach dat co `potwierdzenia`.
 */
import { swieto } from './swieta'

export const RODZAJE = {
  spotkanie: { etykieta: 'Spotkanie', bg: '#E0F2FE', fg: '#075985', kropka: '#0284C7', zakonczonyBg: '#FEF9C3', zakonczonyFg: '#854D0E' },
  montaz: { etykieta: 'Montaż', bg: '#FFEDD5', fg: '#9A3412', kropka: '#EA580C', zakonczonyBg: '#DCFCE7', zakonczonyFg: '#166534' },
  serwis: { etykieta: 'Serwis', bg: '#EDE9FE', fg: '#5B21B6', kropka: '#7C3AED', zakonczonyBg: '#F1F5F9', zakonczonyFg: '#475569' },
}
export const STATUSY = [['zaplanowany', 'Zaplanowany'], ['rozpoczety', 'Rozpoczęty'], ['zakonczony', 'Zakończony'], ['przeniesiony', 'Przeniesiony'], ['odwolany', 'Odwołany']]
/** Statusy terminu do wyboru w oknie: „Rozpoczęty” tylko przy montażu. */
export const statusyRodzaju = (rodzaj) => STATUSY.filter(([s]) => s !== 'rozpoczety' || rodzaj === 'montaz')
/** Termin w toku: zaplanowany albo (montaż) rozpoczęty. */
export const wToku = (t) => t.status === 'zaplanowany' || t.status === 'rozpoczety'
const ISO = /^\d{4}-\d{2}-\d{2}$/
const GODZ = /^\d{1,2}:\d{2}$/

export const isoDnia = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
export const zIso = (iso) => new Date(`${iso}T12:00:00`)
export const dodajDni = (iso, n) => { const d = zIso(iso); d.setDate(d.getDate() + n); return isoDnia(d) }
export const poniedzialek = (iso) => { const d = zIso(iso); return dodajDni(iso, -((d.getDay() + 6) % 7)) }
const MIES = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru']
export const MIESIACE = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień']
const DNI = ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.']
export const krotkaData = (iso) => { const d = zIso(iso); return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}` }
export const opisDaty = (t) => {
  const d = zIso(t.od)
  if (t.do && t.do !== t.od) {
    const k = zIso(t.do)
    return `${DNI[d.getDay()]}–${DNI[k.getDay()].replace('.', '')} ${d.getDate()}–${k.getDate()} ${MIES[k.getMonth()]}`
  }
  return `${DNI[d.getDay()]} ${d.getDate()} ${MIES[d.getMonth()]}`
}

let licznik = 0
export const noweId = () => `t${Date.now().toString(36)}${(licznik += 1)}`

/** Termin w jednym kształcie; nieprawidłowe daty i godziny wypadają, `do` nie wcześniej niż `od`. */
export const normalizujTermin = (t) => {
  const r = t && typeof t === 'object' ? t : {}
  const rodzaj = RODZAJE[r.rodzaj] ? r.rodzaj : 'spotkanie'
  const od = typeof r.od === 'string' && ISO.test(r.od) ? r.od : ''
  let doIso = rodzaj === 'montaz' && typeof r.do === 'string' && ISO.test(r.do) ? r.do : od
  if (doIso < od) doIso = od
  const godz = (v) => (typeof v === 'string' && GODZ.test(v) ? v : '')
  const calyDzien = Boolean(r.calyDzien) || !godz(r.godzOd)
  const status = statusyRodzaju(rodzaj).some(([s]) => s === r.status) ? r.status : 'zaplanowany'
  return {
    id: String(r.id || noweId()), rodzaj, od, do: doIso,
    godzOd: calyDzien ? '' : godz(r.godzOd), godzDo: calyDzien ? '' : godz(r.godzDo), calyDzien,
    miejscowosc: String(r.miejscowosc || ''), ulica: String(r.ulica || ''), kto: String(r.kto || ''),
    status,
    przeniesionyNa: typeof r.przeniesionyNa === 'string' && ISO.test(r.przeniesionyNa) ? r.przeniesionyNa : '',
    ustalony: Boolean(r.ustalony), notatka: String(r.notatka || ''), ustalenia: String(r.ustalenia || ''),
  }
}

export const kalendarzZDanych = (lista) => (Array.isArray(lista) ? lista.map(normalizujTermin).filter((t) => t.od) : [])

/** Stare pojedyncze daty z karty (`dane.terminy`) jako terminy kalendarza; nic, gdy kalendarz już jest. */
export const zTerminowKarty = (dane) => {
  if (Array.isArray(dane?.kalendarz)) return null
  const t = dane?.terminy || {}
  const lista = []
  // Stałe id: kalendarz firmy pokazuje te daty ze skrótu, a karta zapisuje je potem pod tym samym id (bez dubla).
  if (t.spotkanie) lista.push(normalizujTermin({ id: 'stary-spotkanie', rodzaj: 'spotkanie', od: t.spotkanie, ustalony: t.spotkanieUstalone, calyDzien: true }))
  if (t.montaz) lista.push(normalizujTermin({ id: 'stary-montaz', rodzaj: 'montaz', od: t.montaz, ustalony: t.montazUstalony, calyDzien: true }))
  return lista.filter((x) => x.od)
}

/** Terminy klienta ze skrótu listy: kalendarz, a gdy karta go jeszcze nie ma, stare daty (`terminy` ze skrótu). */
export const terminyKlienta = (k) => (Array.isArray(k?.kalendarz) ? kalendarzZDanych(k.kalendarz) : zTerminowKarty({
  terminy: {
    spotkanie: k?.terminy?.spotkanie, spotkanieUstalone: k?.terminy?.spotkanie_ustalone,
    montaz: k?.terminy?.montaz, montazUstalony: k?.terminy?.montaz_ustalony,
  },
}))

/**
 * Zapis terminu z okna: podmiana po id albo dopisanie. Przeniesienie z datą „Przenieś na” zostawia stary termin
 * jako przeniesiony (przerywana ramka w kalendarzu) i dopisuje nowy, zaplanowany, na nową datę (montaż z tą samą
 * długością). Zwraca nową listę.
 */
export const zapiszTermin = (lista, termin) => {
  const t = normalizujTermin(termin)
  let wynik = lista.some((x) => x.id === t.id) ? lista.map((x) => (x.id === t.id ? t : x)) : [...lista, t]
  if (t.status === 'przeniesiony' && t.przeniesionyNa) {
    const dlugosc = (zIso(t.do) - zIso(t.od)) / 864e5
    const nowy = normalizujTermin({ ...t, id: noweId(), od: t.przeniesionyNa, do: dodajDni(t.przeniesionyNa, dlugosc), status: 'zaplanowany',
      przeniesionyNa: '', notatka: `przeniesiony z ${krotkaData(t.od)}${t.notatka ? `. ${t.notatka}` : ''}` })
    wynik = [...wynik.map((x) => (x.id === t.id ? { ...x, notatka: `przeniesiony na ${krotkaData(t.przeniesionyNa)}` } : x)), nowy]
  }
  return wynik
}

const aktywny = (t) => wToku(t) || t.status === 'zakonczony'

/** Najbliższy przyszły termin klienta (zaplanowany albo rozpoczęty montaż), a gdy go nie ma, ostatni zakończony. */
export const najblizszy = (lista, dzis) => {
  const przyszle = lista.filter((t) => t.status === 'rozpoczety' || (t.status === 'zaplanowany' && t.do >= dzis))
    .sort((a, b) => a.od.localeCompare(b.od))
  if (przyszle.length) return { termin: przyszle[0], przyszly: true }
  const zakonczone = lista.filter((t) => t.status === 'zakonczony').sort((a, b) => b.od.localeCompare(a.od))
  return zakonczone.length ? { termin: zakonczone[0], przyszly: false } : null
}

/** Do skrótu, kanbanu i listy: najbliższe spotkanie i montaż (albo ostatnie, gdy nic przed nami), „ustalony” też przy zakończonym. */
export const terminyZKalendarza = (lista, dzis) => {
  const wybierz = (rodzaj) => najblizszy(lista.filter((t) => t.rodzaj === rodzaj), dzis)?.termin || null
  const s = wybierz('spotkanie')
  const m = wybierz('montaz')
  return {
    spotkanie: s?.od || null, spotkanie_ustalone: pewny(s),
    montaz: m?.od || null, montaz_ustalony: pewny(m),
  }
}

/** Pewny termin: umówiony z klientem, rozpoczęty albo już zakończony. */
export const pewny = (t) => Boolean(t && (t.ustalony || t.status === 'rozpoczety' || t.status === 'zakonczony'))

/**
 * Jedno kliknięcie w „Terminach” (runda 4, 2026-10-11): co można potwierdzić przy terminie w toku, którego dzień
 * już nadszedł. Spotkanie i serwis: „Odbyło się” (zakończony). Montaż w dwóch krokach: „Montaż rozpoczęty”, potem
 * „Montaż zakończony”; po ostatnim dniu montażu zaplanowany można od razu zakończyć. Przyszłe terminy nic nie mają
 * (status da się zmienić w oknie terminu).
 */
export const MONTAZ_WCZESNIEJ_DNI = 7

export const potwierdzenia = (t, dzis) => {
  if (!t || !t.od) return []
  // Montaż bywa zaczęty przed terminem, zanim ktoś przestawi kalendarz (właściciel, 2026-10-11): „Montaż rozpoczęty”
  // jest dostępny do tygodnia wcześniej, a `zastosujPotwierdzenie` przesuwa wtedy początek na dziś.
  if (t.rodzaj === 'montaz' && t.status === 'zaplanowany' && t.od > dzis) {
    return t.od <= dodajDni(dzis, MONTAZ_WCZESNIEJ_DNI) ? [{ status: 'rozpoczety', etykieta: 'Montaż rozpoczęty' }] : []
  }
  if (t.od > dzis) return []
  if (t.rodzaj === 'montaz') {
    if (t.status === 'rozpoczety') return [{ status: 'zakonczony', etykieta: 'Montaż zakończony' }]
    if (t.status !== 'zaplanowany') return []
    return [{ status: 'rozpoczety', etykieta: 'Montaż rozpoczęty' },
      ...((t.do || t.od) < dzis ? [{ status: 'zakonczony', etykieta: 'Montaż zakończony' }] : [])]
  }
  return t.status === 'zaplanowany' ? [{ status: 'zakonczony', etykieta: t.rodzaj === 'serwis' ? 'Serwis odbyty' : 'Spotkanie odbyte' }] : []
}

/** Termin po kliknięciu potwierdzenia: montaż rozpoczęty przed terminem zaczyna się dziś (koniec bez zmian). */
export const zastosujPotwierdzenie = (t, status, dzis) => (status === 'rozpoczety' && t.od > dzis
  ? { ...t, status, od: dzis, do: t.do && t.do > dzis ? t.do : dzis }
  : { ...t, status })

/**
 * Kroki terminu w toku do kafelka „Terminy” (runda 6, 2026-10-12, prośba właściciela: „bardzo intuicyjny, prosty
 * kafelek z nowoczesnymi buttonami”). Spotkanie i serwis: „Potwierdź termin” (`ustalony`, umówiony z klientem),
 * potem „Spotkanie odbyte”. Montaż: „Potwierdź termin”, „Rozpocznij montaż”, „Zakończ montaż”. Reguły dat bez
 * zmian, z `potwierdzenia`. `stan` każdego kroku:
 * - `zrobiony`: odhaczony (potwierdzony liczy się też po rozpoczęciu, jak w `pewny`);
 * - `nastepny`: jedyny wyróżniony przycisk, pierwszy dostępny krok do zrobienia;
 * - `dostepny`: też można kliknąć, ale to nie główny ruch (np. po ostatnim dniu montażu od razu „Zakończ montaż”);
 * - `pozniej`: jeszcze nie teraz, z datą `od`, od której przycisk się pojawi (gdy da się ją podać);
 * - `pominiety`: niepotwierdzony termin, którego dzień minął; potwierdzanie przeszłości nic nie wnosi, kafelek go nie
 *   pokazuje.
 */
export const krokiTerminu = (t, dzis) => {
  if (!t?.od || !wToku(t)) return []
  const dostepne = potwierdzenia(t, dzis).map((a) => a.status)
  const odbyty = t.rodzaj === 'serwis' ? 'Serwis odbyty' : 'Spotkanie odbyte'
  const kroki = [
    { id: 'potwierdz', przycisk: 'Potwierdź termin', etykieta: 'Termin potwierdzony', zrobiony: pewny(t), dostepny: t.status === 'zaplanowany' },
    ...(t.rodzaj === 'montaz' ? [
      { id: 'rozpoczety', przycisk: 'Rozpocznij montaż', etykieta: 'Montaż rozpoczęty', zrobiony: t.status === 'rozpoczety',
        dostepny: dostepne.includes('rozpoczety'), od: dodajDni(t.od, -MONTAZ_WCZESNIEJ_DNI) },
      { id: 'zakonczony', przycisk: 'Zakończ montaż', etykieta: 'Montaż zakończony', zrobiony: false,
        dostepny: dostepne.includes('zakonczony'), od: t.status === 'rozpoczety' ? t.od : '' },
    ] : [
      { id: 'zakonczony', przycisk: odbyty, etykieta: odbyty, zrobiony: false, dostepny: dostepne.includes('zakonczony'), od: t.od },
    ]),
  ]
  const pominiety = (k) => k.id === 'potwierdz' && !k.zrobiony && t.od < dzis
  const nastepny = kroki.find((k) => !k.zrobiony && k.dostepny && !pominiety(k))
  return kroki.map((k) => ({
    ...k,
    stan: k.zrobiony ? 'zrobiony' : pominiety(k) ? 'pominiety' : k === nastepny ? 'nastepny' : k.dostepny ? 'dostepny' : 'pozniej',
  }))
}

/** Termin po kliknięciu kroku z `krokiTerminu`: potwierdzenie ustawia `ustalony`, reszta to zmiana statusu. */
export const zastosujKrok = (t, krok, dzis) => (krok === 'potwierdz' ? { ...t, ustalony: true } : zastosujPotwierdzenie(t, krok, dzis))

/** Wszystkie terminy firmy z klientami (do kalendarza), z odfiltrowanymi rodzajami. */
export const terminyFirmy = (klienci, ukryte = {}) => klienci.flatMap((k) => terminyKlienta(k)
  .filter((t) => !ukryte[t.rodzaj]).map((t) => ({ ...t, klient: k })))

/**
 * Siatka miesiąca od poniedziałku: tygodnie po 7 dni, w każdym dniu odcinki terminów. Montaż na kilka dni to ciągły
 * pasek: `poczatek`/`koniec` mówią, czy odcinek zaokrąglić z lewej/prawej (zawija się na poniedziałek).
 */
export const siatkaMiesiaca = (rok, miesiac, terminy) => {
  const pierwszy = isoDnia(new Date(rok, miesiac, 1))
  const ostatni = isoDnia(new Date(rok, miesiac + 1, 0))
  const start = poniedzialek(pierwszy)
  const tygodnie = []
  for (let d = start; d <= ostatni || tygodnie.length === 0 || tygodnie[tygodnie.length - 1].length < 7;) {
    const tydzien = []
    for (let i = 0; i < 7; i += 1) {
      const iso = d
      const odcinki = terminy.filter((t) => t.od <= iso && iso <= t.do).map((t) => ({
        termin: t, iso, wielodniowy: t.do !== t.od, pierwszy: iso === t.od,
        poczatek: iso === t.od || i === 0, koniec: iso === t.do || i === 6,
        dzien: Math.round((zIso(iso) - zIso(t.od)) / 864e5) + 1, dni: Math.round((zIso(t.do) - zIso(t.od)) / 864e5) + 1,
      })).sort((a, b) => Number(b.wielodniowy) - Number(a.wielodniowy) || (a.termin.godzOd || '99').localeCompare(b.termin.godzOd || '99'))
      // `swieto`: nazwa święta ustawowego (dzień wolny od pracy, runda 4) albo null.
      tydzien.push({ iso, wMiesiacu: iso >= pierwszy && iso <= ostatni, weekend: i >= 5, swieto: swieto(iso), odcinki })
      d = dodajDni(d, 1)
    }
    tygodnie.push(tydzien)
    if (d > ostatni) break
  }
  return tygodnie
}

const naGodziny = (g) => { const [h, m] = g.split(':').map(Number); return h + m / 60 }

/** Tydzień: terminy z godziną w siatce godzin (7–18), reszta (cały dzień, kilka dni) w pasie nad siatką. */
export const ukladTygodnia = (poczatekTygodnia, terminy, { od = 7, doGodz = 19 } = {}) => {
  const dni = Array.from({ length: 7 }, (_, i) => dodajDni(poczatekTygodnia, i))
  const koniec = dni[6]
  const wTygodniu = terminy.filter((t) => t.od <= koniec && t.do >= poczatekTygodnia)
  const pas = wTygodniu.filter((t) => t.calyDzien || t.do !== t.od).map((t) => ({
    termin: t, kolOd: Math.max(0, dni.indexOf(t.od < poczatekTygodnia ? poczatekTygodnia : t.od)),
    kolDo: dni.indexOf(t.do > koniec ? koniec : t.do),
  }))
  const zGodzina = wTygodniu.filter((t) => !t.calyDzien && t.do === t.od).map((t) => {
    const p = Math.max(od, naGodziny(t.godzOd))
    const k = Math.min(doGodz, t.godzDo ? Math.max(naGodziny(t.godzDo), p + 0.5) : p + 1)
    return { termin: t, kol: dni.indexOf(t.od), gora: p - od, wysokosc: k - p }
  })
  return { dni, pas, zGodzina, swieta: dni.map(swieto) }
}

/**
 * Lista obok kalendarza (makieta): na górze klienci bez żadnego terminu, niżej zaplanowani posortowani wg najbliższego
 * terminu, a gdy nic przed nimi, wg ostatniego zakończonego (najświeższe wyżej). Odwołane i przeniesione nie liczą się.
 */
export const listaBoczna = (klienci, dzis) => {
  const bez = []
  const zaplanowani = []
  for (const k of klienci) {
    const lista = terminyKlienta(k).filter(aktywny)
    if (!lista.length) { bez.push(k); continue }
    const n = najblizszy(lista, dzis)
    if (!n) { bez.push(k); continue }
    zaplanowani.push({ klient: k, ...n })
  }
  zaplanowani.sort((a, b) => (a.przyszly !== b.przyszly ? (a.przyszly ? -1 : 1)
    : a.przyszly ? a.termin.od.localeCompare(b.termin.od) : b.termin.od.localeCompare(a.termin.od)))
  return { bez, zaplanowani }
}

/** Ile innych terminów firmy przypada w te dni (podpowiedź w oknie planowania). */
export const konflikty = (termin, terminy) => terminy.filter((t) => t.id !== termin.id && aktywny(t)
  && t.od <= termin.do && termin.od <= t.do && (!termin.kto || !t.kto || t.kto === termin.kto))

/** Liczniki do filtrów i chipów nad kalendarzem (w danym zakresie dni). */
export const licznikiZakresu = (terminy, od, doIso) => {
  const w = terminy.filter((t) => t.od <= doIso && t.do >= od)
  const ile = (rodzaj, status) => w.filter((t) => t.rodzaj === rodzaj && (!status || t.status === status)).length
  return {
    spotkanie: ile('spotkanie'), montaz: ile('montaz'), serwis: ile('serwis'),
    spotkanieZakonczone: ile('spotkanie', 'zakonczony'), montazZakonczone: ile('montaz', 'zakonczony'),
    przeniesione: w.filter((t) => t.status === 'przeniesiony').length,
  }
}

/**
 * Godzina z położenia kursora w siatce tygodnia (przeciągnięcie klienta, 2026-10-11): `y` w pikselach od góry
 * siatki, zaokrąglenie do `krok` minut, w widocznym zakresie (ostatni start `krok` przed końcem siatki).
 */
export const godzinaZPozycji = (y, { px, od, doGodz, krok = 15 }) => {
  const minuty = Math.round(((y / px) * 60) / krok) * krok
  const zakres = Math.min(Math.max(minuty, 0), (doGodz - od) * 60 - krok)
  const m = od * 60 + zakres
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** „HH:MM” przesunięte o `minuty` (koniec spotkania domyślnie godzinę po początku), najpóźniej 23:59. */
export const dodajMinuty = (hhmm, minuty = 60) => {
  if (!GODZ.test(hhmm || '')) return ''
  const [h, m] = hhmm.split(':').map(Number)
  const w = Math.min(h * 60 + m + minuty, 23 * 60 + 59)
  return `${String(Math.floor(w / 60)).padStart(2, '0')}:${String(w % 60).padStart(2, '0')}`
}

/** Miejsce terminu z adresu karty (`dane.adres`): miejscowość i „ulica nr domu/nr lokalu”; bez ulicy sam numer. */
export const miejsceZAdresu = (adres) => {
  const a = adres || {}
  const numer = [a.nrDomu, a.nrLokalu].map((x) => String(x || '').trim()).filter(Boolean).join('/')
  return { miejscowosc: String(a.miejscowosc || '').trim(), ulica: [String(a.ulica || '').trim(), numer].filter(Boolean).join(' ') }
}

/** Rodzaje terminów, które dostają domyślnie adres klienta (serwis bywa w biurze albo u innego klienta). */
export const RODZAJE_Z_ADRESEM = ['spotkanie', 'montaz']

/**
 * Nowa data terminu z kliknięcia w małym kalendarzu okna „Zaplanuj termin” (2026-10-11): montaż zachowuje liczbę
 * dni (przesuwa się cały), reszta to jeden dzień.
 */
export const ustawDzien = (t, iso) => {
  if (t.rodzaj !== 'montaz' || !t.od) return { ...t, od: iso, do: iso }
  const dlugosc = Math.max(0, Math.round((zIso(t.do || t.od) - zIso(t.od)) / 864e5))
  return { ...t, od: iso, do: dodajDni(iso, dlugosc) }
}

/**
 * Terminy firmy do okna planowania w karcie klienta: terminy innych klientów ze skrótu listy i bieżące terminy tej
 * karty (mogą mieć niezapisane zmiany, więc wygrywają nad skrótem). Wszystkie z `klient`.
 */
export const terminyFirmyZKarta = (klienci, klient, terminyKarty) => [
  ...terminyFirmy((klienci || []).filter((k) => String(k.id) !== String(klient?.id))),
  ...(terminyKarty || []).map((t) => ({ ...t, klient })),
]

/** Termin z miejscowością do prognozy pogody: własna albo, w starszych terminach bez miejsca, klienta (runda 4). */
export const zMiejscowoscia = (t, miejscowoscKlienta = '') => (t.miejscowosc ? t : { ...t, miejscowosc: miejscowoscKlienta || t.klient?.miejscowosc || '' })

/** Rodzaje terminów, przy których wpisuje się „kto jedzie” (spotkanie każdy umawia sam dla siebie, 2026-10-11). */
export const RODZAJE_Z_EKIPA = ['montaz', 'serwis']
